import { getMonday, getSunday } from './date'

/*
A calendar range has the same shape as the ranges in the statistics context:
    from      - first day
    hoverTo   - last day, set once the range is complete
    enteredTo - last day shown in the calendar (follows the mouse while the last day is being chosen)
*/

const getStartOfDay = (date) => {
    const day = new Date(date)
    day.setHours(0, 0, 0, 0)
    return day
}

const isRangeComplete = (range) => !!(range && range.from && range.hoverTo)

const isSelectingLastDay = (range) => !!(range && range.from && !range.hoverTo)

const getCompleteRange = (from, to) => {
    const start = getStartOfDay(from)
    const end = getStartOfDay(to)

    return { from: start, hoverTo: end, enteredTo: end }
}

/* A click starts a new range, unless the first day is already chosen and the clicked day
is not before it - then it completes the range. Clicking inside a complete range also starts a new one. */
const getRangeAfterDayClick = (range, day) => {
    const clickedDay = getStartOfDay(day)

    if (isSelectingLastDay(range) && clickedDay >= getStartOfDay(range.from)) {
        return getCompleteRange(range.from, clickedDay)
    }

    return { from: clickedDay, hoverTo: null, enteredTo: null }
}

/* Preview of the range while the mouse is over a day; null when nothing should change */
const getRangeAfterDayHover = (range, day) => {
    const hoveredDay = getStartOfDay(day)

    if (!isSelectingLastDay(range) || hoveredDay < getStartOfDay(range.from)) {
        return null
    }

    return { ...range, enteredTo: hoveredDay }
}

/* Monday - Sunday of the week containing the given date */
const getWeekRangeOf = (date) => getCompleteRange(getMonday(new Date(date)), getSunday(new Date(date)))

const getLastWeekRange = (today = new Date()) => {
    const dayLastWeek = new Date(today)
    dayLastWeek.setDate(dayLastWeek.getDate() - 7)

    return getWeekRangeOf(dayLastWeek)
}

const padNumber = (number) => String(number).padStart(2, '0')

/* Date -> "YYYY-MM-DD" (local time) for <input type="date"> */
const toDateInputValue = (date) => {
    if (!date) return ''

    const day = new Date(date)
    return `${day.getFullYear()}-${padNumber(day.getMonth() + 1)}-${padNumber(day.getDate())}`
}

/* "YYYY-MM-DD" -> Date at local midnight, or null when the value is not a real date */
const fromDateInputValue = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '')
    if (!match) return null

    const [year, month, day] = match.slice(1).map(Number)
    const date = new Date(year, month - 1, day)

    // rejects dates like 2026-02-31, which Date would move to March
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null
    }

    return date
}

/* Sets the "from" or "to" date of a range from a date field. Invalid dates are ignored,
a "to" without a "from" becomes a one-day range, and reversed dates are swapped. */
const getRangeWithDate = (range, field, date) => {
    if (!date) return range

    let from = field === 'from' ? date : range.from
    let to = field === 'to' ? date : range.hoverTo

    if (!from) {
        from = to
    }

    if (!to) {
        return { from: getStartOfDay(from), hoverTo: null, enteredTo: null }
    }

    if (getStartOfDay(to) < getStartOfDay(from)) {
        [from, to] = [to, from]
    }

    return getCompleteRange(from, to)
}

export {
    getStartOfDay,
    isRangeComplete,
    isSelectingLastDay,
    getCompleteRange,
    getRangeAfterDayClick,
    getRangeAfterDayHover,
    getWeekRangeOf,
    getLastWeekRange,
    toDateInputValue,
    fromDateInputValue,
    getRangeWithDate,
}
